import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { AccordionBody, CardBody, Col, Row } from "reactstrap";
import { t } from "i18next";

import SessionText from "../SessionText/SessionText";
import ComprehensionReview from "../ComprehensionReview";
import EvaluationSlider from "../EvaluationSlider";
import StudentAudioRecording from "../AudioRecording/StudentAudioRecording";
import TeacherNotes from "../Notes/TeacherNotes";
import SegmentedProgressBar from "../../ProgressBar/SegmentedProgressBar";
import LinearProgressBar from "../../ProgressBar/LinearProgressBar";
import EvaluationCard from "../../../pages/Student/SessionEvaluation/components/EvaluationCard";
import levelIcon from "../../../assets/images/svg/rocket.svg";
import clockIcon from "../../../assets/images/svg/clock.svg";

const TABS = ["accuracy", "fluency", "comprehension"];

const AccuracyDetails = ({ session, textLevel }) => {
  const duration = session?.reading?.duration ?? 0;

  return (
    <Col md={5} className="order-2 order-md-2">
      <div className="session-detail-item">
        <img src={levelIcon} alt="Level" />
        <span>
          {t("profile.textLevelLabel")}:{" "}
          {textLevel?.code || textLevel?.name || "-"}
        </span>
      </div>

      <div className="session-detail-item">
        <img src={clockIcon} alt="Duration" />
        <span>
          {t("profile.readingDurationLabel")}: {duration} {t("session.seconds")}
        </span>
      </div>

      <StudentAudioRecording
        src={session?.reading?.recording}
        title="التسجيل"
      />

      <TeacherNotes
        title={t("session.notes")}
        accuracy={session?.reading?.accuracyNotes}
        comprehension={session?.comprehension?.comprehensionNotes}
      />
    </Col>
  );
};

const SubmittedAssessment = ({ onStart }) => (
  <CardBody>
    <div className="submitted-assessment-body d-flex flex-column align-items-center justify-content-center py-5">
      <h5 className="mb-4 text-center fw-bold text-dark">
        {t("profile.notAssessedYet")}
      </h5>

      <button className="btn btn-start-assessment" onClick={onStart}>
        {t("profile.startAssessment")}
      </button>
    </div>
  </CardBody>
);

const AccordionSessionBody = ({
  session,
  miscueList,
  keyItem,
  assessmentText,
  textLevel,
}) => {
  const [activeTab, setActiveTab] = useState("accuracy");

  const params = useParams();
  const history = useHistory();

  const isAccuracy = activeTab === "accuracy";
  const isFluency = activeTab === "fluency";
  const isComprehension = activeTab === "comprehension";

  const {
    comprehensionScore = 0,
    accuracyScore = 0,
    fluencyScore = 0,
  } = session?.results ?? {};

  const handleStartAssessment = () => {
    const basePath = params.school
      ? `/schools/${params.school}/classrooms/${params.classroom}`
      : `/classrooms/${params.classroom}`;

    history.push(
      `${basePath}/sections/${params.section}/students/${params.student}/sessions/${session?.id}/evaluate`,
    );
  };

  if (session?.status?.toLowerCase() === "submitted") {
    return (
      <AccordionBody accordionId={keyItem}>
        <SubmittedAssessment onStart={handleStartAssessment} />
      </AccordionBody>
    );
  }

  const renderContent = () => {
    if (isComprehension) {
      return (
        <ComprehensionReview
          questionSets={assessmentText?.questionSets}
          questionSetsResults={session?.comprehension?.questionSetsResults}
        />
      );
    }

    if (isFluency) {
      return (
        <EvaluationCard className="text-end">
          <EvaluationSlider
            score={fluencyScore}
            feedback={
              session?.results?.fluencyNotes || session?.reading?.notes || ""
            }
            isEditable={false}
            title="تقييم المعلم لطلاقة القراءة"
          />
        </EvaluationCard>
      );
    }

    return (
      <EvaluationCard className="text-end">
        <div className="d-flex align-items-center justify-content-start gap-3 pb-3 mb-3 border-bottom">
          <img
            src={assessmentText?.coverPhoto}
            alt={session?.title}
            className="book-thumbnail-cover"
            style={{
              width: 64,
              height: 64,
              borderRadius: 8,
              objectFit: "cover",
            }}
          />

          <h4 className="book-assessment-title mb-0 fw-bold">
            {session?.title}
          </h4>
        </div>

        <SessionText
          session={session}
          miscueList={miscueList}
          sessionAssessment={assessmentText}
        />
      </EvaluationCard>
    );
  };

  return (
    <AccordionBody accordionId={keyItem}>
      <CardBody className="px-4">
        <div className="accordion-metrics-row">
          <LinearProgressBar
            title={t("profile.accuracyTab")}
            color="#10b981"
            value={accuracyScore}
          />

          <SegmentedProgressBar
            title={t("profile.comprehensionTab")}
            count={9}
            value={comprehensionScore}
          />

          <LinearProgressBar
            title={t("profile.fluencyTab")}
            color="#10b981"
            value={fluencyScore}
          />
        </div>

        <div className="assessment-tabs-container">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`assessment-tab-item ${
                activeTab === tab ? "active" : ""
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {t(`profile.${tab}Tab`)}
            </button>
          ))}
        </div>

        <div className="assessment-body-content mt-4">
          <Row>
            {isAccuracy && (
              <AccuracyDetails session={session} textLevel={textLevel} />
            )}

            <Col
              md={isAccuracy ? 7 : 12}
              className="order-1 order-md-1 mb-4 mb-md-0"
            >
              {renderContent()}
            </Col>
          </Row>
        </div>
      </CardBody>
    </AccordionBody>
  );
};

export default AccordionSessionBody;
